import type { SpaceKindId, SurfaceId, WallMode } from './types'

/** A surface finish: how it reads in 2D (fill color) and is grouped in the picker. */
export interface SurfaceDef {
  id: SurfaceId
  label: string
  category: 'indoor' | 'outdoor'
  /** Base color used for the 2D fill (drawn at low opacity) and as a 3D albedo seed. */
  color: string
  /** Optional hint for a 2D/3D pattern overlay (stone path, grass blades, etc.). */
  pattern?: 'path' | 'blades' | 'speckle' | 'planks' | 'ripple'
}

export const SURFACES: SurfaceDef[] = [
  // indoor
  { id: 'tile', label: 'Tile', category: 'indoor', color: '#c3cad6' },
  { id: 'wood', label: 'Wood', category: 'indoor', color: '#b08968', pattern: 'planks' },
  { id: 'marble', label: 'Marble', category: 'indoor', color: '#e8e6e1' },
  { id: 'concrete', label: 'Concrete', category: 'indoor', color: '#9aa0a6' },
  { id: 'carpet', label: 'Carpet', category: 'indoor', color: '#a98b8b' },
  { id: 'grippyTile', label: 'Grippy tile', category: 'indoor', color: '#8d99ae', pattern: 'speckle' },
  // outdoor
  { id: 'grass', label: 'Grass', category: 'outdoor', color: '#5e9e63', pattern: 'blades' },
  { id: 'grassStonePath', label: 'Grass + stone path', category: 'outdoor', color: '#6aa86a', pattern: 'path' },
  { id: 'stonePath', label: 'Stone path', category: 'outdoor', color: '#9a9488', pattern: 'path' },
  { id: 'paver', label: 'Paver', category: 'outdoor', color: '#b0a99a' },
  { id: 'gravel', label: 'Gravel', category: 'outdoor', color: '#b6b0a4', pattern: 'speckle' },
  { id: 'mud', label: 'Mud (tree bed)', category: 'outdoor', color: '#8a6d4b', pattern: 'speckle' },
  { id: 'water', label: 'Water', category: 'outdoor', color: '#5b8bb0', pattern: 'ripple' },
  { id: 'woodDeck', label: 'Wood deck', category: 'outdoor', color: '#b98a5e', pattern: 'planks' },
]

export const SURFACE_BY_ID: Record<SurfaceId, SurfaceDef> = Object.fromEntries(
  SURFACES.map((s) => [s.id, s]),
) as Record<SurfaceId, SurfaceDef>

/** A space-kind preset: the enclosure defaults a newly-drawn space of this kind gets. */
export interface SpaceKindDef {
  id: SpaceKindId
  label: string
  description: string
  wallMode: WallMode
  hasCeiling: boolean
  defaultSurface: SurfaceId
}

export const SPACE_KINDS: SpaceKindDef[] = [
  {
    id: 'room',
    label: 'Room',
    description: 'Full walls + ceiling — an enclosed room.',
    wallMode: 'full',
    hasCeiling: true,
    defaultSurface: 'tile',
  },
  {
    id: 'coveredArea',
    label: 'Covered area',
    description: 'Waist-height walls + ceiling — covered balcony / utility.',
    wallMode: 'half',
    hasCeiling: true,
    defaultSurface: 'tile',
  },
  {
    id: 'terrace',
    label: 'Terrace',
    description: 'Waist-height walls, open to the sky.',
    wallMode: 'half',
    hasCeiling: false,
    defaultSurface: 'grippyTile',
  },
  {
    id: 'portico',
    label: 'Portico / carport',
    description: 'Roof overhead, no walls.',
    wallMode: 'none',
    hasCeiling: true,
    defaultSurface: 'concrete',
  },
  {
    id: 'openArea',
    label: 'Open area',
    description: 'No walls, no ceiling — garden, driveway, lawn, tree bed.',
    wallMode: 'none',
    hasCeiling: false,
    defaultSurface: 'grass',
  },
]

export const SPACE_KIND_BY_ID: Record<SpaceKindId, SpaceKindDef> = Object.fromEntries(
  SPACE_KINDS.map((k) => [k.id, k]),
) as Record<SpaceKindId, SpaceKindDef>

/** Default name stem for a new space of a given kind. */
export function defaultSpaceName(kind: SpaceKindId, index: number): string {
  const base: Record<SpaceKindId, string> = {
    room: 'Room',
    coveredArea: 'Covered area',
    terrace: 'Terrace',
    portico: 'Portico',
    openArea: 'Area',
  }
  return `${base[kind]} ${index}`
}
